/**
 * Hook useTenantPermissionProfiles
 *
 * Wrapper React Query da RPC `get_permission_profiles_list` já existente,
 * retornando apenas perfis ATIVOS do tenant atual no formato { id, name },
 * pronto para popular seletores (ex.: restrição de calendário por perfil).
 *
 * RELEASE:
 *   2026-06-25 v1.0.0 - Versão inicial.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useTenantPermissionProfiles');

export interface TenantPermissionProfile {
  id: string;
  name: string;
}

export function useTenantPermissionProfiles(enabled: boolean = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const query = useQuery({
    queryKey: ['tenant-permission-profiles', tenantId],
    enabled: enabled && !!tenantId,
    staleTime: 60_000,
    queryFn: async (): Promise<TenantPermissionProfile[]> => {
      logger.info('Listando perfis de permissão do tenant');
      const { data, error } = await supabase.rpc('get_permission_profiles_list', {
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar perfis de permissão', error);
        throw error;
      }
      return (data ?? [])
        .filter((p: any) => p.is_active)
        .map((p: any) => ({ id: p.id as string, name: p.name as string }));
    },
  });

  return {
    profiles: query.data ?? [],
    isLoading: query.isLoading,
  };
}
